import { useLanguage } from '../context/LanguageContext';
import { Check, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

export default function LanguageSelector({ className = '', compact = false, onDarkBg = false }) {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  
  // Fetch enabled languages from backend
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API}/languages/enabled`);
        if (response.data && response.data.length > 0) {
          setAvailableLanguages(response.data);
        } else {
          // Fallback to English if no languages configured
          setAvailableLanguages([{ code: 'en', name: 'English', native_name: 'English' }]);
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
        // Fallback to English
        setAvailableLanguages([{ code: 'en', name: 'English', native_name: 'English' }]);
      } finally {
        setLoading(false);
      }
    };
    fetchLanguages();
  }, []);
  
  const currentLang = availableLanguages.find(l => l.code === language) || availableLanguages[0] || { code: 'en', name: 'English' };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't show selector if only one language or loading
  if (loading || availableLanguages.length <= 1) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors text-sm font-medium uppercase tracking-wide ${
          onDarkBg
            ? 'text-white hover:bg-white/15'
            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-500/10'
        }`}
        data-testid="language-selector-btn"
      >
        <span>{(currentLang.code || 'en').toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl overflow-hidden z-50 min-w-[140px]">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left text-sm ${
                language === lang.code 
                  ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400' 
                  : 'text-gray-700 dark:text-gray-200'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="font-medium">{lang.name}</span>
              {language === lang.code && (
                <Check className="w-4 h-4 text-brand-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
